import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';

import { formsV2 as Forms } from '@breezeway/brzw-components';

const ENTER_KEY_CODE = 13;
const YEAR_FORMAT = 'YYYY';

const parseStringToDate = (string, minDate) => {
  const currentYear = moment().format(YEAR_FORMAT);

  let parsedDate = Date.parse(string);

  if (_.isNaN(parsedDate)) {
    parsedDate = Date.parse(`${string} ${currentYear}`);
  }

  const momentDate = moment(parsedDate);
  const isValidDate = !_.isEmpty(string) && momentDate.isValid();

  if (isValidDate) {
    const selectedYear = momentDate.format(YEAR_FORMAT);
    const isYearSelectedByUser = _.includes(string, selectedYear);

    if (!isYearSelectedByUser) momentDate.set('year', _.toInteger(currentYear));

    if (momentDate.isValid()) {
      if (minDate && momentDate.isBefore(minDate)) {
        return minDate;
      } else {
        return momentDate.toDate();
      }
    }
  }

  return null;
};

class CustomDayPickerInput extends Component {
  constructor(props) {
    super(props);

    this.state = {
      inputValue: props.value,
    };
  }

  componentDidUpdate(prevProps) {
    const { value, selectedDate } = this.props;
    if (prevProps.selectedDate !== selectedDate) this.changeInputValue(value);
  }

  componentWillUnmount() {
    this.removeEnterListener();
    this.removeClickListener();
  }

  handleEnter = event => {
    const { onChangeDate, inputRef, minDate } = this.props;
    const { inputValue } = this.state;

    if (event.keyCode === ENTER_KEY_CODE) {
      const date = parseStringToDate(inputValue, minDate);

      onChangeDate(date);
      _.invoke(inputRef, 'current.blur');

      if (!date) this.changeInputValue('');
    }
  };

  handleClick = event => {
    const { value, wrapperRef, onChangeDate, minDate } = this.props;
    const { inputValue } = this.state;
    const target = _.get(event, 'target');
    const clickedOutsidePicker = !_.invoke(wrapperRef, 'current.contains', target);
    const shouldClearForm = clickedOutsidePicker && _.isEmpty(inputValue) && !_.isEmpty(value);

    if (shouldClearForm) {
      onChangeDate(null);
    } else if (clickedOutsidePicker) {
      const date = parseStringToDate(inputValue, minDate);
      onChangeDate(date);
    }
  };

  addEnterListener = () => document.addEventListener('keydown', this.handleEnter);

  removeEnterListener = () => document.removeEventListener('keydown', this.handleEnter);

  addClickListener = () => document.addEventListener('mousedown', this.handleClick);

  removeClickListener = () => document.removeEventListener('mousedown', this.handleClick);

  changeInputValue = inputValue => {
    const { setHighlightedDate, minDate } = this.props;
    if (!_.isEmpty(_.trim(inputValue))) {
      const date = parseStringToDate(inputValue);
      if (moment(date).isAfter(moment(minDate), 'day')) {
        setHighlightedDate(date);
      } else {
        setHighlightedDate(null);
      }
    }
    this.setState({
      inputValue,
    });
  };

  onFocusInput = () => {
    const { inputRef, onFocus } = this.props;

    onFocus();
    _.invoke(inputRef, 'current.select');
    this.addEnterListener();
    this.addClickListener();
  };

  onBlurInput = () => {
    const { onBlur, value } = this.props;

    onBlur();
    this.removeEnterListener();
    this.removeClickListener();

    this.changeInputValue(value);
  };

  render() {
    const {
      isMobile,
      error,
      icon,
      id,
      inputRef,
      label,
      onChangeDate,
      onClick,
      placeholder,
      readOnly,
      required,
      theme,
      withClearAction,
    } = this.props;
    const { inputValue } = this.state;

    const withError = !_.isEmpty(error);
    const onClearInput = () => {
      onChangeDate(null);
    };

    return (
      <Forms.Input
        additionalSubText={error}
        disabled={readOnly}
        iconAfter={withError && 'info-outline'}
        iconBefore={icon}
        id={id}
        inputProps={{
          id,
          onClick,
          readOnly: isMobile,
        }}
        inputRef={inputRef}
        isRequired={required}
        labelText={label}
        onBlurInput={this.onBlurInput}
        onChange={this.changeInputValue}
        onClearInput={onClearInput}
        onFocusInput={this.onFocusInput}
        placeholderText={placeholder}
        size="compact"
        theme={theme}
        value={inputValue}
        withClearAction={withClearAction}
        withError={withError}
      />
    );
  }
}

CustomDayPickerInput.propTypes = {
  error: PropTypes.string,
  icon: PropTypes.string,
  id: PropTypes.string,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  isMobile: PropTypes.bool,
  label: PropTypes.string,
  minDate: PropTypes.shape({}),
  onBlur: PropTypes.func,
  onChangeDate: PropTypes.func,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  selectedDate: PropTypes.shape({}),
  setHighlightedDate: PropTypes.func,
  theme: PropTypes.string,
  value: PropTypes.string,
  withClearAction: PropTypes.bool,
  wrapperRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
};

export default CustomDayPickerInput;
